import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"

import { get } from "../../lib/request"

import { enrichMessageWithUserActions } from "../../utils/messageUserActions"



function normalizeInteractionMessages(apiMessages) {

  return (Array.isArray(apiMessages) ? apiMessages : []).map(enrichMessageWithUserActions)

}

function isOptimisticMessage(message) {
  const id = String(message?.id ?? "")
  return id.startsWith("temp-") || Boolean(message?.tempId)
}

function isOutboundInteractionMessage(message) {
  if (message?.direction === "OUTBOUND") return true
  if (message?.direction === "INBOUND") return false
  if (message?.author != null && message?.pageSocialNetworkId != null) {
    return String(message.author) === String(message.pageSocialNetworkId)
  }
  return false
}

function normalizeMessageContent(text) {
  return String(text ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
}

function messageHasAttachments(message) {
  return Array.isArray(message?.attachments) && message.attachments.length > 0
}

function messageMatchKey(message) {
  const text = normalizeMessageContent(message?.content ?? message?.text)
  if (text) return `text:${text}`
  const att = message?.attachments?.[0]
  const url = att?.link || att?.url
  if (url) return `att:${String(url)}`
  return ""
}

/** Match optimistic outbound rows to API rows when preview blob URLs differ from CDN links. */
function findMatchingOutboundApi(optimistic, outboundApi, usedApiIds) {
  if (!isOptimisticMessage(optimistic)) return null

  const optText = normalizeMessageContent(optimistic?.content ?? optimistic?.text)
  const optHasAtt = messageHasAttachments(optimistic)
  const optTime = messageTimestampMs(optimistic)
  const optParent =
    optimistic?.parentId != null && String(optimistic.parentId).trim() !== ''
      ? String(optimistic.parentId)
      : null

  const candidates = outboundApi.filter((api) => {
    if (api?.id != null && usedApiIds.has(api.id)) return false

    const apiText = normalizeMessageContent(api?.content ?? api?.text)
    if (apiText !== optText) return false

    const apiHasAtt = messageHasAttachments(api)
    if (optHasAtt !== apiHasAtt) return false
    if (!optHasAtt) return optText !== ''

    if (optParent) {
      const apiParent =
        api?.parentId != null && String(api.parentId).trim() !== ''
          ? String(api.parentId)
          : null
      if (apiParent && apiParent !== optParent) return false
    }

    const apiTime = messageTimestampMs(api)
    if (optTime > 0 && apiTime > 0 && Math.abs(apiTime - optTime) > 30 * 60 * 1000) {
      return false
    }

    return true
  })

  if (!candidates.length) return null

  candidates.sort((a, b) => {
    const da = Math.abs(messageTimestampMs(a) - optTime)
    const db = Math.abs(messageTimestampMs(b) - optTime)
    return da - db
  })

  return candidates[0]
}

function messageTimestampMs(message) {
  const raw = message?.sourceTimestamp || message?.createdAt
  const ts = raw ? new Date(raw).getTime() : NaN
  return Number.isFinite(ts) ? ts : 0
}

function sortInteractionMessages(messages) {
  return [...messages].sort(
    (a, b) => messageTimestampMs(a) - messageTimestampMs(b),
  )
}

/** One row per API id / sourceId; drop optimistic when API already has same outbound content. */
function dedupeInteractionMessages(messages) {
  const real = []
  const optimistic = []
  for (const m of Array.isArray(messages) ? messages : []) {
    if (!m) continue
    if (isOptimisticMessage(m)) optimistic.push(m)
    else real.push(m)
  }

  const out = []
  const seenIds = new Set()
  const seenOutboundContent = new Set()

  for (const m of real) {
    const keys = []
    if (m.id != null && String(m.id).trim() !== "") keys.push(`id:${m.id}`)
    if (m.sourceId != null && String(m.sourceId).trim() !== "") keys.push(`src:${m.sourceId}`)
    if (keys.some((k) => seenIds.has(k))) continue
    keys.forEach((k) => seenIds.add(k))
    if (isOutboundInteractionMessage(m)) {
      const contentKey = messageMatchKey(m)
      if (contentKey) seenOutboundContent.add(contentKey)
    }
    out.push(m)
  }

  for (const m of optimistic) {
    const contentKey = messageMatchKey(m)
    if (contentKey && seenOutboundContent.has(contentKey)) continue
    if (
      isOutboundInteractionMessage(m)
      && findMatchingOutboundApi(m, real.filter(isOutboundInteractionMessage), new Set())
    ) {
      continue
    }
    const keys = []
    if (m.id != null && String(m.id).trim() !== "") keys.push(`id:${m.id}`)
    if (keys.some((k) => seenIds.has(k))) continue
    keys.forEach((k) => seenIds.add(k))
    out.push(m)
  }

  return out
}

/** Drop optimistic placeholders once the server returns the real outbound message. */
function pendingOptimisticMessages(existingMessages, apiMessages) {
  const optimisticOnly = (Array.isArray(existingMessages) ? existingMessages : []).filter(
    isOptimisticMessage,
  )
  if (!optimisticOnly.length) return []

  const outboundApi = (Array.isArray(apiMessages) ? apiMessages : []).filter(
    isOutboundInteractionMessage,
  )
  const usedApiIds = new Set()

  const stillPending = []

  for (const optimistic of [...optimisticOnly].sort(
    (a, b) => messageTimestampMs(a) - messageTimestampMs(b),
  )) {
    const key = messageMatchKey(optimistic)
    let match = key
      ? outboundApi.find((api) => !usedApiIds.has(api.id) && messageMatchKey(api) === key)
      : null

    if (!match) {
      match = findMatchingOutboundApi(optimistic, outboundApi, usedApiIds)
    }

    if (match?.id != null) {
      usedApiIds.add(match.id)
      continue
    }

    stillPending.push(optimistic)
  }

  const maxPendingAgeMs = 2 * 60 * 1000
  const now = Date.now()
  return stillPending.filter((optimistic) => {
    const age = now - messageTimestampMs(optimistic)
    return age >= 0 && age < maxPendingAgeMs
  })
}



function parseInteractionMessagesFetchArg(arg) {
  if (arg != null && typeof arg === "object" && !Array.isArray(arg)) {
    return {
      clientId: arg.clientId ?? arg.client_id ?? null,
      interactionId: arg.interactionId ?? arg.interaction_id ?? null,
      force: Boolean(arg.force),
      apiOnly: Boolean(arg.apiOnly),
      allowDuringReplySync: Boolean(arg.allowDuringReplySync),
    }
  }
  return {
    clientId: null,
    interactionId: arg,
    force: false,
    apiOnly: false,
    allowDuringReplySync: false,
  }
}

function isReplySyncBlockingFetch(getState, interactionId, allowDuringReplySync) {
  if (allowDuringReplySync) return false
  const slice = getState().interactionMessages
  const intId = interactionId != null ? String(interactionId).trim() : ""
  if (!intId || !slice?.replySyncInteractionId) return false
  if (String(slice.replySyncInteractionId) !== intId) return false
  return Number(slice.replySyncUntilMs ?? 0) > Date.now()
}



function resolveClientId(getState, explicitClientId) {

  if (explicitClientId != null && String(explicitClientId).trim() !== "") {

    return explicitClientId

  }

  const selected = getState().clients?.selectedClient

  const id = selected?.id ?? selected?.clientId

  if (id != null && String(id).trim() !== "") return id

  return null

}



// GET /api/v1/messages/clients/{clientId}/interactions/{interactionId}

export const fetchMessagesByInteractionId = createAsyncThunk(

  "interactionMessages/fetchByInteractionId",

  async (arg, { rejectWithValue, getState }) => {

    try {

      const token = getState().auth?.token

      if (!token) throw new Error("Authentication required")



      const { clientId: argClientId, interactionId, force } = parseInteractionMessagesFetchArg(arg)

      if (!interactionId) {

        throw new Error("interactionId is required")

      }



      const clientId = resolveClientId(getState, argClientId)

      if (!clientId) {

        throw new Error("clientId is required")

      }



      const data = await get(

        `messages/clients/${clientId}/interactions/${interactionId}${force ? `?_=${Date.now()}` : ''}`,

        force ? { cache: 'no-store' } : {},

      )

      return { interactionId, clientId, data }

    } catch (err) {

      return rejectWithValue(err?.message || "Failed to fetch interaction messages")

    }

  },

  {
    condition: (arg, { getState }) => {
      const {
        clientId: argClientId,
        interactionId,
        force,
        allowDuringReplySync,
      } = parseInteractionMessagesFetchArg(arg)
      if (isReplySyncBlockingFetch(getState, interactionId, allowDuringReplySync)) {
        return false
      }
      if (force) return true
      const intId = interactionId != null ? String(interactionId).trim() : ""
      if (!intId) return false
      const clientId = resolveClientId(getState, argClientId)
      const cid = clientId != null ? String(clientId) : ""
      const s = getState().interactionMessages
      if (
        (s.status === "loading" || s.status === "refreshing") &&
        String(s.interactionId ?? "") === intId &&
        String(s.clientId ?? "") === cid
      ) {
        return false
      }
      if (
        s.status === "succeeded" &&
        String(s.interactionId ?? "") === intId &&
        String(s.clientId ?? "") === cid &&
        Array.isArray(s.messages) &&
        s.messages.length > 0
      ) {
        return false
      }
      return true
    },
  },

)



const initialState = {

  interactionId: null,

  clientId: null,

  messages: [],

  status: "idle", // idle | loading | succeeded | failed

  error: null,

  /** While set, block interaction GET except the scheduled post-reply sync fetch. */
  replySyncInteractionId: null,
  replySyncUntilMs: 0,

}



const interactionMessagesSlice = createSlice({

  name: "interactionMessages",

  initialState,

  reducers: {

    clearInteractionMessages: () => initialState,



    addOptimisticMessage: (state, action) => {

      state.messages.push(action.payload)

    },



    removeOptimisticMessage: (state, action) => {
      state.messages = state.messages.filter(
        (m) => m.id !== action.payload,
      )
    },

    clearOptimisticMessages: (state) => {
      state.messages = state.messages.filter((m) => !isOptimisticMessage(m))
    },

    startReplySync: (state, action) => {
      const interactionId = action.payload?.interactionId
      const delayMs = Number(action.payload?.delayMs) || 6000
      state.replySyncInteractionId =
        interactionId != null ? String(interactionId) : null
      state.replySyncUntilMs = Date.now() + delayMs
    },

    endReplySync: (state) => {
      state.replySyncInteractionId = null
      state.replySyncUntilMs = 0
    },

  },



  extraReducers: (builder) => {

    builder

      .addCase(fetchMessagesByInteractionId.pending, (state, action) => {

        const { interactionId } = parseInteractionMessagesFetchArg(action.meta.arg)

        const sameInteraction =

          interactionId != null &&

          state.interactionId != null &&

          String(state.interactionId) === String(interactionId)

        state.status =

          sameInteraction && state.messages.length > 0 ? "refreshing" : "loading"

        state.error = null

      })

      .addCase(fetchMessagesByInteractionId.fulfilled, (state, action) => {
        state.status = "succeeded"

        const { apiOnly, allowDuringReplySync } = parseInteractionMessagesFetchArg(
          action.meta.arg,
        )
        const { interactionId, clientId, data } = action.payload
        const apiMessages = normalizeInteractionMessages(
          Array.isArray(data) ? data : data ? [data] : [],
        )
        const sortedApi = sortInteractionMessages(dedupeInteractionMessages(apiMessages))

        if (String(state.interactionId ?? '') !== String(interactionId ?? '')) {
          state.messages = sortedApi
          state.interactionId = interactionId
          state.clientId = clientId
          if (apiOnly || allowDuringReplySync) {
            state.replySyncInteractionId = null
            state.replySyncUntilMs = 0
          }
          return
        }

        state.clientId = clientId

        if (apiOnly || allowDuringReplySync) {
          state.messages = sortedApi
          state.replySyncInteractionId = null
          state.replySyncUntilMs = 0
          return
        }

        const stillPending = pendingOptimisticMessages(state.messages, sortedApi)
        const existingReal = (state.messages || []).filter((m) => !isOptimisticMessage(m))
        // Union-merge so a slower in-flight GET cannot drop an auto-reply already applied.
        state.messages = sortInteractionMessages(
          dedupeInteractionMessages([...sortedApi, ...existingReal, ...stillPending]),
        )
      })



      .addCase(fetchMessagesByInteractionId.rejected, (state, action) => {

        state.status = "failed"

        state.error = action.payload

      })

  },

})

export const {
  clearInteractionMessages,
  addOptimisticMessage,
  removeOptimisticMessage,
  clearOptimisticMessages,
  startReplySync,
  endReplySync,
} = interactionMessagesSlice.actions

export default interactionMessagesSlice.reducer

